export const NETWORK_GRAPH_WIDTH = 960;
export const NETWORK_GRAPH_HEIGHT = 640;
export const NETWORK_GRAPH_MIN_DISTANCE = 52;

export type NetworkGraphSector = 'top' | 'right' | 'bottom' | 'left';
export type NetworkGraphNodeSize = 'primary' | 'active' | 'default';

export interface NetworkGraphLayoutNode {
  id: string;
  size: NetworkGraphNodeSize;
  groupSector?: NetworkGraphSector;
}

export interface NetworkGraphLayoutEdge {
  sourceId: string;
  targetId: string;
}

export interface NetworkGraphPosition {
  x: number;
  y: number;
}

export type NetworkGraphPositions = Record<string, NetworkGraphPosition>;

export interface NetworkGraphLayoutSize {
  width: number;
  height: number;
}

export interface ComputeRadialLayoutOptions {
  expandedNodeIds?: readonly string[];
  size?: NetworkGraphLayoutSize;
  primaryNodeId?: string;
  // Existing coordinates can be supplied when graph data grows. Nodes already
  // on the canvas keep those coordinates; only newly discovered nodes are laid
  // out, so activating a peripheral node cannot move the node that was clicked.
  previousPositions?: Readonly<NetworkGraphPositions>;
  // When a new node has several parents, these newly expanded anchors get first
  // claim before the primary-ring pass. Existing visible nodes stay pinned.
  prioritizeAroundNodeIds?: readonly string[];
  prioritizeNodeIds?: readonly string[];
}

export interface ComputeRadialLayoutResult {
  positions: NetworkGraphPositions;
  fannedNodeIds: readonly string[];
  // Placement ray per node (degrees): the anchor→node direction the layout
  // fanned the node out at. Single source of truth for everything that must
  // align with the node's arrow (node labels); a node kept on a cached
  // position is absent here and keeps its previously stored ray.
  rayAngles: Record<string, number>;
}

const DEFAULT_LAYOUT_SIZE: NetworkGraphLayoutSize = {
  width: NETWORK_GRAPH_WIDTH,
  height: NETWORK_GRAPH_HEIGHT,
};
const SECTORS: NetworkGraphSector[] = ['top', 'right', 'bottom', 'left'];
const SECTOR_ANGLES: Record<NetworkGraphSector, number> = {
  top: -90,
  right: 0,
  bottom: 90,
  left: 180,
};
// Radii scale with the smaller viewport edge so the graph fills any container
// (near-square page canvas or 3:2 Storybook frame) without letterboxing.
const PRIMARY_RING_FACTOR = 0.34;
const EXPANSION_RADIUS_FACTOR = 0.22;
const EXPANSION_RING_GAP_FACTOR = 0.13;
const SEPARATION_STEP = 16;
// An expanded node fans its children across an arc centered on the outward
// direction (away from the graph center), so a peripheral node opens into open
// space instead of wrapping the fan back over the graph behind it. The arc
// grows with child count — min(EXPANSION_FAN_ARC, (count - 1) * FAN_STEP) — so
// a two-child fan spreads a comfortable ~60° (product + company no longer
// huddle). The cap is the outward half-plane: no child ever lands behind the
// expanded node, so a dense fan cannot wrap back over the ring it came from.
// Fans too large for one ring at that arc fill concentric rings instead of
// growing the radius without bound (see wedgeFanSlots).
const EXPANSION_FAN_ARC = 180;
const FAN_STEP = 60;
// A fan keeps today's single chord-fit ring while that radius stays within
// this many ring gaps of the base radius; past it the wedge fills concentric
// rings, so arrow length grows ~O(√count) instead of linearly with count.
const SINGLE_RING_MAX_GAPS = 2;

const degreesToRadians = (degrees: number) => (degrees * Math.PI) / 180;
const radiansToDegrees = (radians: number) => (radians * 180) / Math.PI;
const round = (value: number) => Math.round(value * 1000) / 1000;

const positionAt = (
  origin: NetworkGraphPosition,
  angle: number,
  radius: number
): NetworkGraphPosition => {
  const radians = degreesToRadians(angle);

  return {
    x: round(origin.x + Math.cos(radians) * radius),
    y: round(origin.y + Math.sin(radians) * radius),
  };
};

const distanceBetween = (
  first: NetworkGraphPosition,
  second: NetworkGraphPosition
) => Math.hypot(first.x - second.x, first.y - second.y);

// Smallest radius at which two nodes `step` degrees apart still keep
// NETWORK_GRAPH_MIN_DISTANCE (the chord constraint solved for the radius).
// The 0.1% margin keeps rounded positions strictly clear of the minimum so
// separatePosition never nudges an exact-chord ring member off its radius.
const chordFitRadius = (step: number) =>
  step >= 360
    ? 0
    : (NETWORK_GRAPH_MIN_DISTANCE * 1.001) /
      (2 * Math.sin(degreesToRadians(Math.min(step, 180)) / 2));

// Inverse of chordFitRadius: the smallest angular step (degrees) at which two
// nodes on a ring of `radius` still keep NETWORK_GRAPH_MIN_DISTANCE.
const chordFitAngle = (radius: number) =>
  2 *
  radiansToDegrees(
    Math.asin(Math.min(1, (NETWORK_GRAPH_MIN_DISTANCE * 1.001) / (2 * radius)))
  );

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

// Shortest signed rotation from `angle` to `target`, normalized to (-180°, 180°]
// so a top-sector child of a bottom anchor clamps to the nearest fan edge rather
// than spinning the long way around.
const signedAngleDelta = (angle: number, target: number) =>
  ((((target - angle) % 360) + 540) % 360) - 180;

interface RingSlot {
  angle: number;
  radius: number;
}

// Proportional ring: each present group gets a contiguous angular span sized by
// its member count (a lone group takes the full circle, two groups split it
// 20/24 vs 4/24, etc.), so empty sectors leave no dead gaps. Spans are laid out
// contiguously in SECTORS order and the whole ring is rotated so every span
// sits as close to its home SECTOR_ANGLES direction as the count-weighted
// circular-mean fit allows. Members spread evenly across their span on ONE
// shared radius — the smallest that keeps NETWORK_GRAPH_MIN_DISTANCE between
// ring neighbors — so arrows within a group all have the same length and stay
// angularly separated instead of crossing. Huge groups therefore grow the
// radius rather than stacking rings; the canvas is pan/zoomable by design.
const proportionalRingSlots = (
  groups: ReadonlyMap<NetworkGraphSector, readonly NetworkGraphLayoutNode[]>,
  baseRadius: number
): Map<string, RingSlot> => {
  const present = SECTORS.flatMap(sector => {
    const members = groups.get(sector) ?? [];

    return members.length > 0 ? [{ sector, members }] : [];
  });
  const total = present.reduce((sum, group) => sum + group.members.length, 0);
  const slots = new Map<string, RingSlot>();
  if (total === 0) {
    return slots;
  }

  let prefix = 0;
  const spans = present.map(group => {
    const span = (360 * group.members.length) / total;
    const start = prefix;
    prefix += span;

    return { ...group, start, span };
  });
  // Count-weighted circular mean of each span's home-angle deviation: the
  // single rotation that best points every group at its home sector.
  let sinSum = 0;
  let cosSum = 0;
  for (const { sector, members, start, span } of spans) {
    const delta = degreesToRadians(
      signedAngleDelta(start + span / 2, SECTOR_ANGLES[sector])
    );
    sinSum += members.length * Math.sin(delta);
    cosSum += members.length * Math.cos(delta);
  }
  const rotation = radiansToDegrees(Math.atan2(sinSum, cosSum));

  for (const { members, start, span } of spans) {
    const step = span / members.length;
    const radius = Math.max(baseRadius, chordFitRadius(step));
    members.forEach((node, index) => {
      slots.set(node.id, {
        angle: rotation + start + (index + 0.5) * step,
        radius,
      });
    });
  }

  return slots;
};

// Concentric-ring wedge fill for fans too large for one ring: inner rings fill
// to chord capacity, the outermost takes the remainder, and each ring spreads
// its members across segment-centered slots (offset half a step from the arc
// edges). Segment-centering keeps ring edges from stacking radially, so a
// spoke to an outer-ring child doesn't run straight through the inner children
// sitting on the wedge boundary. Slots come back sorted by angle so
// sector-ordered children keep their angular order across rings.
// spokes to outer rings can still pass close to inner-ring nodes at
// coincidental alignments; per-spoke routing is the upgrade path if that ever
// reads badly in practice.
interface WedgeFanOptions {
  count: number;
  centerAngle: number;
  arc: number;
  baseRadius: number;
  ringGap: number;
}

const wedgeFanSlots = ({
  count,
  centerAngle,
  arc,
  baseRadius,
  ringGap,
}: WedgeFanOptions): RingSlot[] => {
  const slots: RingSlot[] = [];
  let remaining = count;
  for (let ring = 0; remaining > 0; ring += 1) {
    const radius = baseRadius + ring * ringGap;
    const capacity = Math.max(1, Math.floor(arc / chordFitAngle(radius)));
    const members = Math.min(remaining, capacity);
    const step = arc / members;
    for (let index = 0; index < members; index += 1) {
      slots.push({
        angle: centerAngle - arc / 2 + (index + 0.5) * step,
        radius,
      });
    }
    remaining -= members;
  }

  return slots.toSorted((first, second) => first.angle - second.angle);
};

const byId = (first: NetworkGraphLayoutNode, second: NetworkGraphLayoutNode) =>
  first.id.localeCompare(second.id);

const separatePosition = (
  candidate: NetworkGraphPosition,
  origin: NetworkGraphPosition,
  existingPositions: NetworkGraphPosition[]
): NetworkGraphPosition => {
  const deltaX = candidate.x - origin.x;
  const deltaY = candidate.y - origin.y;
  const magnitude = Math.hypot(deltaX, deltaY) || 1;
  const direction = { x: deltaX / magnitude, y: deltaY / magnitude };
  let separated = candidate;

  // this outward nudge is sufficient for dozens of nodes; dense
  // graphs can replace it with d3-force behind computeRadialLayout's signature.
  while (
    existingPositions.some(
      position =>
        distanceBetween(position, separated) < NETWORK_GRAPH_MIN_DISTANCE
    )
  ) {
    separated = {
      x: round(separated.x + direction.x * SEPARATION_STEP),
      y: round(separated.y + direction.y * SEPARATION_STEP),
    };
  }

  return separated;
};

const connectedNodeIds = (
  nodeId: string,
  edges: readonly NetworkGraphLayoutEdge[]
): Set<string> => {
  const connectedIds = new Set<string>();

  for (const edge of edges) {
    if (edge.sourceId === nodeId) {
      connectedIds.add(edge.targetId);
    } else if (edge.targetId === nodeId) {
      connectedIds.add(edge.sourceId);
    }
  }

  return connectedIds;
};

const sectorFor = (
  node: NetworkGraphLayoutNode,
  fallbackIndex: number
): NetworkGraphSector =>
  node.groupSector ?? SECTORS[fallbackIndex % SECTORS.length];

// To [-180°, 180°) so stored rays compare cleanly regardless of how many
// turns the slot arithmetic accumulated.
const normalizeAngle = (angle: number) => (((angle % 360) + 540) % 360) - 180;

interface PlaceNodeOptions {
  positions: NetworkGraphPositions;
  occupiedPositions: NetworkGraphPosition[];
  rayAngles: Record<string, number>;
  nodeId: string;
  candidate: NetworkGraphPosition;
  origin: NetworkGraphPosition;
  // The anchor→candidate direction this slot was fanned out at. Recorded with
  // the position (separatePosition only slides ALONG this ray, so it stays
  // true) as the single alignment truth for the node's label.
  rayAngle: number;
}

const placeNode = ({
  positions,
  occupiedPositions,
  rayAngles,
  nodeId,
  candidate,
  origin,
  rayAngle,
}: PlaceNodeOptions) => {
  const position = separatePosition(candidate, origin, occupiedPositions);
  positions[nodeId] = position;
  rayAngles[nodeId] = normalizeAngle(rayAngle);
  occupiedPositions.push(position);
};

// Single source of truth for the graph's center / focus origin: an explicit
// primaryNodeId, else the primary-sized node, else the first node. Hover-edge
// resolution reuses this so it treats the same node as the origin the layout
// centers on (otherwise omitting primaryNodeId silently disables hover-edge
// reveal for a primary-sized hub the layout still centers).
export const resolvePrimaryNode = <T extends NetworkGraphLayoutNode>(
  nodes: readonly T[],
  primaryNodeId?: string
): T | undefined =>
  (primaryNodeId ? nodes.find(node => node.id === primaryNodeId) : undefined) ??
  nodes.find(node => node.size === 'primary') ??
  nodes[0];

export const computeRadialLayoutResult = (
  nodes: readonly NetworkGraphLayoutNode[],
  edges: readonly NetworkGraphLayoutEdge[],
  {
    expandedNodeIds = [],
    size = DEFAULT_LAYOUT_SIZE,
    // The center node keeps its position after it stops being the biggest node,
    // so activating a child never reshuffles the graph. Falls back to the
    // primary-sized node, then the first node.
    primaryNodeId,
    previousPositions,
    prioritizeAroundNodeIds = [],
    prioritizeNodeIds = [],
  }: ComputeRadialLayoutOptions = {}
): ComputeRadialLayoutResult => {
  const primaryNode = resolvePrimaryNode(nodes, primaryNodeId);
  if (!primaryNode) {
    return { positions: {}, fannedNodeIds: [], rayAngles: {} };
  }

  const CENTER: NetworkGraphPosition = {
    x: size.width / 2,
    y: size.height / 2,
  };
  const minEdge = Math.min(size.width, size.height);
  const PRIMARY_RING_RADIUS = minEdge * PRIMARY_RING_FACTOR;
  const EXPANSION_RADIUS = minEdge * EXPANSION_RADIUS_FACTOR;
  const EXPANSION_RING_GAP = minEdge * EXPANSION_RING_GAP_FACTOR;
  const expandedNodeIdSet = new Set(expandedNodeIds);
  const prioritizedAnchorIdSet = new Set(prioritizeAroundNodeIds);
  const priorityEligibleNodeIdSet = new Set(prioritizeNodeIds);
  const prioritizedNodeIdSet = new Set(
    prioritizeAroundNodeIds
      .flatMap(anchorNodeId => [...connectedNodeIds(anchorNodeId, edges)])
      .filter(
        connectedNodeId =>
          connectedNodeId !== primaryNode.id &&
          !expandedNodeIdSet.has(connectedNodeId) &&
          priorityEligibleNodeIdSet.has(connectedNodeId)
      )
  );

  const positions: NetworkGraphPositions = {};
  const rayAngles: Record<string, number> = {};
  const occupiedPositions = Object.values(previousPositions ?? {});
  const fannedNodeIds: string[] = [];
  for (const node of nodes) {
    const previousPosition = previousPositions?.[node.id];
    if (previousPosition && !prioritizedNodeIdSet.has(node.id)) {
      positions[node.id] = previousPosition;
    }
  }
  if (!positions[primaryNode.id]) {
    positions[primaryNode.id] = CENTER;
    occupiedPositions.push(CENTER);
  }
  const primaryConnectionIds = connectedNodeIds(primaryNode.id, edges);
  const primaryConnections = nodes
    .filter(
      node => node.id !== primaryNode.id && primaryConnectionIds.has(node.id)
    )
    .toSorted(byId);
  const sectorGroups = new Map(
    SECTORS.map(sector => [sector, [] as NetworkGraphLayoutNode[]])
  );

  primaryConnections.forEach((node, index) => {
    sectorGroups.get(sectorFor(node, index))?.push(node);
  });

  // Slots are computed from the full sorted membership (including pinned or
  // prioritized members) so a node's ring position never depends on which
  // siblings happen to already be placed.
  const ringSlots = proportionalRingSlots(sectorGroups, PRIMARY_RING_RADIUS);
  for (const node of primaryConnections) {
    const slot = ringSlots.get(node.id);
    if (!slot || positions[node.id] || prioritizedNodeIdSet.has(node.id)) {
      continue;
    }

    placeNode({
      positions,
      occupiedPositions,
      rayAngles,
      nodeId: node.id,
      candidate: positionAt(CENTER, slot.angle, slot.radius),
      origin: CENTER,
      rayAngle: slot.angle,
    });
  }

  const expansionOrder = [
    ...expandedNodeIds.filter(nodeId => prioritizedAnchorIdSet.has(nodeId)),
    ...expandedNodeIds.filter(nodeId => !prioritizedAnchorIdSet.has(nodeId)),
  ];
  for (const expandedNodeId of expansionOrder) {
    const expandedPosition = positions[expandedNodeId];
    if (!expandedPosition) {
      continue;
    }

    const connectedIds = connectedNodeIds(expandedNodeId, edges);
    const newConnections = nodes
      .filter(node => connectedIds.has(node.id) && !positions[node.id])
      .toSorted(byId);
    if (newConnections.length === 0) {
      continue;
    }
    fannedNodeIds.push(expandedNodeId);

    // Open the fan into the outward direction (away from the center) so a
    // peripheral node fans its children into open space instead of wrapping the
    // fan back over the graph behind it. Children are ordered by their sector's
    // global angle (clamped into the fan arc), not by id, so a product child
    // lands on the right-most side the geometry allows, a company child toward
    // the bottom, etc. Dense fans may extend beyond the initial frame; the
    // viewport supports pan/zoom to reach them.
    const outwardAngle = radiansToDegrees(
      Math.atan2(expandedPosition.y - CENTER.y, expandedPosition.x - CENTER.x)
    );
    const count = newConnections.length;
    const arc = Math.min(EXPANSION_FAN_ARC, (count - 1) * FAN_STEP);
    const halfArc = arc / 2;
    const angleStep = count > 1 ? arc / (count - 1) : 0;
    const orderedChildren = newConnections
      .map(node => {
        const idealAngle = node.groupSector
          ? SECTOR_ANGLES[node.groupSector]
          : outwardAngle;
        const clampedAngle =
          outwardAngle +
          clamp(signedAngleDelta(outwardAngle, idealAngle), -halfArc, halfArc);

        return { node, clampedAngle };
      })
      .toSorted(
        (first, second) =>
          first.clampedAngle - second.clampedAngle ||
          SECTORS.indexOf(first.node.groupSector ?? 'top') -
            SECTORS.indexOf(second.node.groupSector ?? 'top') ||
          byId(first.node, second.node)
      );

    // One shared radius while the fan fits a single ring near the base radius:
    // siblings keep their minimum distance without staggering and arrows stay
    // uniform. Once the chord-fit radius balloons past that, concentric wedge
    // rings hold the overflow at bounded arrow lengths instead.
    const ringGap = Math.max(EXPANSION_RING_GAP, NETWORK_GRAPH_MIN_DISTANCE);
    const singleRingRadius = Math.max(
      EXPANSION_RADIUS,
      chordFitRadius(angleStep || 360)
    );
    const slots: RingSlot[] =
      singleRingRadius <= EXPANSION_RADIUS + SINGLE_RING_MAX_GAPS * ringGap
        ? orderedChildren.map((_, index) => ({
            angle: outwardAngle - halfArc + index * angleStep,
            radius: singleRingRadius,
          }))
        : wedgeFanSlots({
            count,
            centerAngle: outwardAngle,
            arc,
            baseRadius: EXPANSION_RADIUS,
            ringGap,
          });

    // Grow the whole structure out past local congestion so its relative
    // geometry stays intact and a node parked near others fans out past them.
    let radiusShift = 0;
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const collides = slots.some(slot => {
        const candidate = positionAt(
          expandedPosition,
          slot.angle,
          slot.radius + radiusShift
        );

        return occupiedPositions.some(
          position =>
            distanceBetween(position, candidate) < NETWORK_GRAPH_MIN_DISTANCE
        );
      });
      if (!collides) {
        break;
      }
      radiusShift += ringGap;
    }

    orderedChildren.forEach(({ node }, index) => {
      const slot = slots[index];
      placeNode({
        positions,
        occupiedPositions,
        rayAngles,
        nodeId: node.id,
        candidate: positionAt(
          expandedPosition,
          slot.angle,
          slot.radius + radiusShift
        ),
        origin: expandedPosition,
        rayAngle: slot.angle,
      });
    });
  }

  const remainingNodes = nodes
    .filter(node => !positions[node.id])
    .toSorted(byId);
  if (remainingNodes.length > 0) {
    const remainingGroups = new Map(
      SECTORS.map(sector => [sector, [] as NetworkGraphLayoutNode[]])
    );
    remainingNodes.forEach((node, index) => {
      remainingGroups.get(sectorFor(node, index))?.push(node);
    });

    // Rare leftovers (no path from the primary or an expanded anchor) get their
    // own proportional ring one gap outside the primary one; separatePosition
    // nudges past any primary-ring member that grew beyond the base radius.
    const remainingSlots = proportionalRingSlots(
      remainingGroups,
      PRIMARY_RING_RADIUS + EXPANSION_RING_GAP
    );
    for (const node of remainingNodes) {
      const slot = remainingSlots.get(node.id);
      if (!slot) {
        continue;
      }

      placeNode({
        positions,
        occupiedPositions,
        rayAngles,
        nodeId: node.id,
        candidate: positionAt(CENTER, slot.angle, slot.radius),
        origin: CENTER,
        rayAngle: slot.angle,
      });
    }
  }

  return { positions, fannedNodeIds, rayAngles };
};

export const computeRadialLayout = (
  nodes: readonly NetworkGraphLayoutNode[],
  edges: readonly NetworkGraphLayoutEdge[],
  options: ComputeRadialLayoutOptions = {}
): NetworkGraphPositions =>
  computeRadialLayoutResult(nodes, edges, options).positions;
